import type { ComponentProps } from 'react';
import React, { useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import { throttle } from 'lodash-es';
import {
  alignItems,
  backgroundColor,
  border,
  borderRadius,
  display,
  height,
  justifyContent,
  overflow,
  position,
  right,
  space,
  themeGet,
  top,
  width,
} from 'styled-system';
import type {
  AlignItemsProps,
  BackgroundColorProps,
  BorderProps,
  BorderRadiusProps,
  DisplayProps,
  FlexProps,
  HeightProps,
  JustifyContentProps,
  OverflowProps,
  PositionProps,
  RightProps,
  TopProps,
  WidthProps,
} from 'styled-system';

import type { OutlineProps, SpaceProps } from 'common/theme/system-types';
import { useLocalization } from '../../providers/LocaleProvider';
import { outlineCSS } from '../../theme/styles';
import { cursor, outline, outlineColor } from '../../theme/system-utilities';
import type { CursorProps, OutlineColorProps } from '../../theme/system-utilities';
import { trackClearSearchClick, trackSearchInputClick } from '../../tracking/filters';
import { Button } from '../Button';
import { Flex } from '../Flex';
import Icon from '../Icon';
import type { InputProps } from '../Input/StatefulInput/types';
import { BodyRegularBR1 } from '../Typography';

const DEBOUNCE_TIMEOUT = 3000;

interface FilterSearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
  name?: string;
  filterName?: string;
}

const SearchContainer = styled(Flex)<WidthProps & PositionProps & SpaceProps>`
  ${width};
  ${position};
  ${space};
`;

const searchContainerStyles: ComponentProps<typeof SearchContainer> = {
  width: 1,
  position: 'relative',
  my: 'sp_8',
  px: 'sp_8',
};

const Search = styled(BodyRegularBR1)<
  PositionProps &
    DisplayProps &
    FlexProps &
    SpaceProps &
    HeightProps &
    BackgroundColorProps &
    BorderRadiusProps &
    WidthProps &
    OutlineProps &
    OutlineColorProps &
    CursorProps &
    OverflowProps &
    BorderProps &
    InputProps
>`
  ${position};
  ${space};
  ${height};
  ${backgroundColor};
  ${borderRadius};
  ${width};
  ${outline};
  ${outlineColor};
  ${overflow};
  ${border};

  &:hover {
    outline-color: ${themeGet('colors.sys.neutral.border.hover')};
    color: ${themeGet('colors.sys.neutral.text.hover')};

    &::placeholder {
      color: ${themeGet('colors.sys.neutral.text.hover')};
    }

    svg {
      fill: ${themeGet('colors.sys.neutral.text.hover')};
    }
  }

  &:focus-within:not(:has(button:focus)) {
    ${outlineCSS};
  }

  &::placeholder {
    color: ${themeGet('colors.sys.neutral.text.default')};
  }

  &[type='search'] {
    -webkit-appearance: none;

    &:invalid {
      box-shadow: none !important;
    }

    &::-ms-clear {
      display: none;
    }

    &::-webkit-search-decoration,
    &::-webkit-search-cancel-button,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration {
      -webkit-appearance: none;
    }
  }
`;

const searchStyles: ComponentProps<typeof Search> = {
  as: 'input',
  type: 'search',
  display: 'flex',
  width: 1,
  height: '48px',
  overflow: 'hidden',
  backgroundColor: 'sys.neutral.background.weak',
  border: 'none',
  outline: '1px solid',
  outlineColor: 'sys.neutral.border.default',
  borderRadius: 'x-large',
  pl: 'sp_20',
  pr: 'sp_64',
  py: 'sp_6',
};

const FilterIcon = styled(Button)<
  DisplayProps &
    PositionProps &
    AlignItemsProps &
    JustifyContentProps &
    CursorProps &
    WidthProps &
    HeightProps &
    SpaceProps &
    BorderRadiusProps &
    RightProps &
    TopProps
>`
  ${height};
  ${width};
  ${cursor};
  ${borderRadius};
  ${display};
  ${alignItems};
  ${justifyContent};
  ${space};
  ${position};
  ${right};
  ${top};

  &:focus-visible {
    ${outlineCSS};
    outline-offset: -10px;
  }
`;

const filterIconStyles: ComponentProps<typeof FilterIcon> = {
  kind: 'bare',
  alignItems: 'center',
  justifyContent: 'center',
  width: '48px',
  height: '48px',
  borderRadius: 'x-large',
  position: 'absolute',
  right: '10px',
  px: 'sp_16',
};

const FilterSearchField = ({
  value,
  onChange,
  placeholder,
  id,
  filterName,
}: FilterSearchFieldProps) => {
  const { t } = useLocalization();

  const throttledTrackSearchClick = useMemo(
    () => throttle((name: string) => trackSearchInputClick(name), DEBOUNCE_TIMEOUT),
    []
  );

  const handleSearchInputClick = useCallback(() => {
    throttledTrackSearchClick(filterName || '');
  }, [filterName, throttledTrackSearchClick]);

  const handleClearButtonClick = () => {
    trackClearSearchClick(filterName || '');
    onChange('');
  };

  return (
    <SearchContainer {...searchContainerStyles}>
      <Search
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        id={id}
        aria-label={placeholder}
        data-testid={id}
        onClick={handleSearchInputClick}
        {...searchStyles}
      />

      <FilterIcon
        {...filterIconStyles}
        data-testid={`${id}-icon`}
        {...(value
          ? {
              onClick: handleClearButtonClick,
              ariaLabel: t('h24_clear_search'),
            }
          : { 'aria-hidden': 'true', tabIndex: -1, cursor: 'auto' })}
      >
        <Icon name={value ? 'clear' : 'searchFillSmall'} size={value ? 16 : 15} />
      </FilterIcon>
    </SearchContainer>
  );
};

export default FilterSearchField;
